import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { generatePath, Outlet, useNavigate, useParams } from 'react-router-dom';
import { pollDeviceData, renameSegment, stopPollDeviceData } from 'commons/src/actions/DeviceActions';
import { analyticsLogger, PageType } from 'commons/src/analytics';
import { DEVICE_VIEWED_DETAILS } from 'commons/src/analytics/AnalyticsEvents';
import { pageNotFoundUrl } from 'commons/src/components/errorComponents/PageNotFound';
import PageHeader from 'commons/src/components/headers/PageHeader';
import { paths, sensorGraphPeriods } from 'commons/src/constants';
import DeviceEditNameHeader from 'commons/src/features/devicePage/DeviceEditNameHeader';
import { hasFeatureToggle } from 'commons/src/features/featureToggle/filterOnFeatureToggles';
import { FeatureToggleEnum, RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { FullDeviceData, DeviceWithKeyInfo } from 'commons/src/models/commonTypeScript';
import { TabOption } from 'commons/src/models/menuModels';
import { Store } from 'commons/src/reducers';
import { ConsumerDeviceStorageKeys } from './deviceOverviewConstants';

export type DevicePageOutletContext = {
    /** Only present when feature toggle customThresholdsB2c is enabled. */
    showThresholdLines?: boolean;
    setShowThresholdLines?: (value: boolean) => void;
    scaleGraphToThresholds?: boolean;
    setScaleGraphToThresholds?: (value: boolean) => void;
    showLegendOnLeft?: boolean;
    setShowLegendOnLeft?: (value: boolean) => void;
    /** Increment to force sensor rows to remount and use default heights (clears stored row heights). */
    sensorLayoutKey: number;
    resetSensorRowHeights: () => void;
};

export type Props = {
    devicePageDevices: { [serialNumber: string]: FullDeviceData };
    devices: { [serialNumber: string]: DeviceWithKeyInfo };
    devicesLoading: boolean;
    fetching: boolean;
    loadingApp: boolean;
    isLoggedIn: boolean;
    featureToggles?: FeatureToggleEnum[];
};

export const DevicePageComponent = ({
    isLoggedIn,
    devicePageDevices,
    loadingApp,
    fetching,
    devicesLoading,
    devices,
    featureToggles,
}: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const { serialNumber } = useParams() as { serialNumber: string };
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const deviceKeyInfo = devices[serialNumber];
    const [showThresholdLines, setShowThresholdLinesState] = useState(
        () => localStorage.getItem(ConsumerDeviceStorageKeys.showThresholdLines) === 'true'
    );
    const [scaleGraphToThresholds, setScaleGraphToThresholdsState] = useState(
        () => localStorage.getItem(ConsumerDeviceStorageKeys.scaleGraphToThresholds) === 'true'
    );
    const [showLegendOnLeft, setShowLegendOnLeftState] = useState(
        () => localStorage.getItem(ConsumerDeviceStorageKeys.showLegendOnLeft) === 'true'
    );
    const setScaleGraphToThresholds = (value: boolean): void => {
        setScaleGraphToThresholdsState(value);
        localStorage.setItem(ConsumerDeviceStorageKeys.scaleGraphToThresholds, String(value));
    };
    const setShowLegendOnLeft = (value: boolean): void => {
        setShowLegendOnLeftState(value);
        localStorage.setItem(ConsumerDeviceStorageKeys.showLegendOnLeft, String(value));
    };
    const setShowThresholdLines = (value: boolean): void => {
        setShowThresholdLinesState(value);
        localStorage.setItem(ConsumerDeviceStorageKeys.showThresholdLines, String(value));
        if (value) {
            setScaleGraphToThresholdsState(true);
            localStorage.setItem(ConsumerDeviceStorageKeys.scaleGraphToThresholds, 'true');
            setShowLegendOnLeftState(true);
            localStorage.setItem(ConsumerDeviceStorageKeys.showLegendOnLeft, 'true');
        } else {
            setScaleGraphToThresholdsState(false);
            localStorage.setItem(ConsumerDeviceStorageKeys.scaleGraphToThresholds, 'false');
            setShowLegendOnLeftState(false);
            localStorage.setItem(ConsumerDeviceStorageKeys.showLegendOnLeft, 'false');
        }
    };

    const [sensorLayoutKey, setSensorLayoutKey] = useState(0);
    const resetSensorRowHeights = (): void => {
        localStorage.removeItem(ConsumerDeviceStorageKeys.sensorRowHeights);
        localStorage.setItem(ConsumerDeviceStorageKeys.showThresholdLines, 'false');
        localStorage.setItem(ConsumerDeviceStorageKeys.scaleGraphToThresholds, 'false');
        localStorage.setItem(ConsumerDeviceStorageKeys.showLegendOnLeft, 'false');
        setShowThresholdLinesState(false);
        setScaleGraphToThresholdsState(false);
        setShowLegendOnLeftState(false);
        setSensorLayoutKey(k => k + 1);
    };

    useEffect(() => {
        if (serialNumber === undefined) {
            navigate(pageNotFoundUrl('serialNumberNotDefined'));
        } else if (!(loadingApp && !isLoggedIn) && !fetching) {
            dispatch(
                pollDeviceData({
                    serialNumber,
                    selectedInterval: sensorGraphPeriods.week,
                    fetching: !devicePageDevices[serialNumber],
                    loading: !devicePageDevices[serialNumber],
                })
            );
        }
        analyticsLogger(DEVICE_VIEWED_DETAILS, { pageType: PageType.Device });
        return (): void => {
            dispatch(stopPollDeviceData());
        };
    }, [isLoggedIn]);

    useEffect(() => {
        const hasDeviceInList = !!devices[serialNumber];
        const hasDevicePageData = !!devicePageDevices[serialNumber];
        const shouldRedirect =
            (fetching && serialNumber === undefined) ||
            (!devicesLoading && !fetching && !hasDeviceInList && !hasDevicePageData);
        if (shouldRedirect) {
            navigate(pageNotFoundUrl('serialNumberNotDefinedOrNotInDevices', { serialNumber }));
        }
    }, [serialNumber, devicePageDevices, devices, fetching, devicesLoading, navigate]);

    const renameDevice = (name: string): void => {
        const segmentId = 'latest';
        dispatch(renameSegment(serialNumber, segmentId, name, deviceKeyInfo.locationId));
    };

    const onBackClicked = (): void => {
        navigate(`/${paths.devices}`);
    };

    const tabOptions: TabOption[] = [
        {
            text: 'Overview',
            id: 'deviceOverview',
            path: generatePath('/devices/:serialNumber', { serialNumber }),
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            testAttr: 'device-overview',
            route: 'devices/:serialNumber',
            requiredGroupTypes: [],
        },
        {
            text: 'Settings',
            id: 'deviceSettings',
            path: generatePath('/devices/:serialNumber/change-location', { serialNumber }),
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            testAttr: 'device-settings',
            route: 'devices/:serialNumber/change-location',
            requiredGroupTypes: [],
        },
    ];
    return (
        <div className="single-device">
            <PageHeader
                title={deviceKeyInfo && deviceKeyInfo.segmentName}
                subHeaderClick={onBackClicked}
                subHeader={txt('AllDevices')}
                customHeader={
                    <DeviceEditNameHeader
                        name={deviceKeyInfo && deviceKeyInfo.segmentName}
                        renameSegment={renameDevice}
                    />
                }
                tabs={tabOptions}
            />
            <Outlet
                context={{
                    ...(hasFeatureToggle(FeatureToggleEnum.customThresholdsB2c, featureToggles)
                        ? {
                              showThresholdLines,
                              setShowThresholdLines,
                              scaleGraphToThresholds,
                              setScaleGraphToThresholds,
                              showLegendOnLeft,
                              setShowLegendOnLeft,
                          }
                        : {}),
                    sensorLayoutKey,
                    resetSensorRowHeights,
                }}
            />
        </div>
    );
};

const mapStateToProps = (state: Store): Props => {
    const {
        devicePage: { devices: devicePageDevices, fetching },
        devices: { devicesWithKeyInfo, loading: devicesLoading },
        app: { loading: loadingApp },
        login: { isLoggedIn },
        userSettings: { featureToggles },
    } = state;
    return {
        devices: devicesWithKeyInfo,
        devicePageDevices,
        fetching,
        devicesLoading,
        loadingApp,
        isLoggedIn,
        featureToggles,
    };
};

export default connect(mapStateToProps)(DevicePageComponent);
